import { useState } from "react";
import { Download, FileText } from "lucide-react";
import { API } from "../lib/api";
import { Reveal } from "../lib/ui";

const AUDIENCES = [
  ["investor", "A funding partner", "Leads with the gap in the market, the per-site model and the published method."],
  ["public", "A public body", "Leads with land, licensing and partnership, and what a county gets."],
  ["self", "My own analysis", "Leads with labels, sources and what every model excludes."],
  ["general", "Someone else", "The plan in plain terms, start to finish."],
];

/** One document, four framings. The reader says who they are and the PDF leads with what they need. */
export const ExecutiveSummary = () => {
  const [audience, setAudience] = useState("investor");
  const url = `${API}/executive-summary.pdf?audience=${audience}`;

  return (
    <section className="border-t border-zinc-900 bg-[#0b0b0b] py-16 md:py-20" data-testid="executive-summary">
      <div className="mx-auto max-w-[1200px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Executive summary</p>
          <h2 className="display-2 mt-4 max-w-4xl">One Document, Written For You</h2>
          <p className="body-copy mt-6 max-w-3xl">
            The 18/47 county model, the facility economics, FC Nairobi, the diaspora and the funding strategy in
            one summary. Tell us who is reading and it leads with what matters to them — the facts underneath do
            not change.
          </p>
        </Reveal>

        <div className="mt-10 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map(([id, label, note]) => (
            <button
              key={id}
              type="button"
              onClick={() => setAudience(id)}
              data-testid={`summary-audience-${id}`}
              className={`flex h-full flex-col items-start gap-2 p-5 text-left transition-colors ${
                audience === id ? "bg-maasai text-white" : "bg-black text-zinc-400 hover:text-white"
              }`}
            >
              <FileText size={16} className={audience === id ? "text-white" : "text-ochre"} />
              <span className="font-display text-base uppercase leading-tight tracking-tight">{label}</span>
              <span className={`text-xs leading-relaxed ${audience === id ? "text-white/80" : "text-zinc-500"}`}>
                {note}
              </span>
            </button>
          ))}
        </div>

        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          data-testid="summary-download"
          className="btn-primary mt-8 inline-flex items-center gap-2"
        >
          <Download size={14} /> Download the executive summary
        </a>
        <p className="mono mt-4 max-w-2xl text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
          Generated live from the platform's own data, so the document can never contradict the site. Figures
          marked as estimates are published to be argued with.
        </p>
      </div>
    </section>
  );
};
